import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AdminLayout from '@/components/AdminLayout'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { getStatusIcon, getStatusText, statusFilterOptions } from '@/utils/status'
import { ArrowLeft, Search, ChevronLeft, ChevronRight, Loader2, FileDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { formatLocalDate } from '@/utils/dateUtils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { CHEAT_TYPE_MAP } from '@/utils/cheatEventUtils'
import { toast } from 'sonner'
import { fetchAllRows } from '@/utils/supabasePagination'

// Define types for clarity
type SortOption =
  | 'newest'
  | 'oldest'
  | 'nameAsc'
  | 'nameDesc'
  | 'scoreHigh'
  | 'scoreLow'
  | 'cheatHigh'
  | 'cheatLow'

const AdminTestCandidates = () => {
  const { testId } = useParams()
  const navigate = useNavigate()
  const [assignments, setAssignments] = useState<any[]>([])
  const [testName, setTestName] = useState('')
  const [loading, setLoading] = useState(true)
  const [exportLoading, setExportLoading] = useState(false)
  const [fetchingCheatEvents, setFetchingCheatEvents] = useState(false)
  const [cheatEventsCache, setCheatEventsCache] = useState<Record<string, Record<string, number>>>(
    {}
  )

  // Filter & Pagination State
  const [searchInput, setSearchInput] = useState('')
  const [magazaInput, setMagazaInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [magazaQuery, setMagazaQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [showMagazaSuggestions, setShowMagazaSuggestions] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')

  // Fetch test name once when testId changes
  useEffect(() => {
    if (!testId) return
    supabase
      .from('tests')
      .select('name')
      .eq('id', testId)
      .single()
      .then(({ data }) => {
        if (data) setTestName(data.name)
      })
  }, [testId])

  // Search handler: apply pending inputs as active server-side filters
  const handleSearch = () => {
    setSearchTerm(searchInput)
    setMagazaQuery(magazaInput)
    setCurrentPage(1)
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!testId) return
      setLoading(true)

      // Build candidate query — filter on embedded employee to avoid long URL from pre-fetching IDs
      const selectStr = searchTerm || magazaQuery
        ? `
          id,
          created_at,
          test_id,
          started_at,
          completed_at,
          assignment_start_date,
          assignment_due_date,
          status,
          attempt_count,
          total_cheat_events,
          test:tests (
            categories,
            total_questions,
            time_limit
          ),
          employee:employee_id!inner (
            id,
            personel,
            sicil,
            magaza
          ),
          employee_answers!employee_test_id (
            question_id,
            choice_id,
            is_correct,
            answered_at,
            question:question_id (
              text,
              category
            )
          )
        `
        : `
          id,
          created_at,
          test_id,
          started_at,
          completed_at,
          assignment_start_date,
          assignment_due_date,
          status,
          attempt_count,
          total_cheat_events,
          test:tests (
            categories,
            total_questions,
            time_limit
          ),
          employee:employee_id (
            id,
            personel,
            sicil,
            magaza
          ),
          employee_answers!employee_test_id (
            question_id,
            choice_id,
            is_correct,
            answered_at,
            question:question_id (
              text,
              category
            )
          )
        `

      let data: any[]
      try {
        data = await fetchAllRows<any>('employee_tests', selectStr, (q: any) => {
          q = q.eq('test_id', testId).neq('status', 'renewed')
          if (statusFilter !== 'all') {
            q = q.eq('status', statusFilter as any)
          }
          if (searchTerm) {
            q = q.or(`personel.ilike.%${searchTerm}%,sicil.ilike.%${searchTerm}%`, {
              referencedTable: 'employee',
            })
          }
          if (magazaQuery) {
            q = q.ilike('employee.magaza', `%${magazaQuery}%`)
          }
          return q
        })
      } catch (err) {
        console.error('Error fetching assignments:', err)
        setLoading(false)
        return
      }

      // 3. Process Scores
      const processedData = (data || []).map((t: any) => {
        // A. Parse Categories
        let categories: Record<string, { weight: number; no_questions: number }> = {}
        const rawCat = t.test?.categories
        if (typeof rawCat === 'string') {
          try {
            categories = JSON.parse(rawCat)
          } catch (e) {}
        } else if (rawCat && typeof rawCat === 'object') {
          categories = rawCat
        }

        // B. Map Responses
        const responses =
          t.employee_answers?.map((a: any) => ({
            questionId: a.question_id,
            category: a.question?.category,
            isCorrect: a.is_correct,
          })) || []

        // C. Question stats
        const totalQuestions = t.test?.total_questions || 0

        const answers = t.employee_answers || []
        const totalSeen = answers.length
        const answeredCount = answers.filter((a: any) => a.choice_id !== null).length

        const totalCorrect = answers.filter((a: any) => a.is_correct === true).length
        const wrongCount = Math.max(answeredCount - totalCorrect, 0)
        const blankCount = Math.max((totalQuestions || totalSeen) - answeredCount, 0)

        // D. Calculate Raw Score
        const rawScore = totalQuestions ? Math.round((totalCorrect / totalQuestions) * 100) : 0

        // E. Calculate Category Stats and Weighted Score
        interface CatStat {
          correct: number
          total: number
          weight: number
          percent: number
        }
        const catStats: Record<string, CatStat> = {}
        let weightedScore = 0

        if (Object.keys(categories).length > 0) {
          for (const [catName, { weight, no_questions }] of Object.entries(categories)) {
            const resp = responses.filter((r: any) => r.category === catName)
            const correct = resp.filter((r: any) => r.isCorrect).length
            const percent = no_questions ? Math.round((correct / no_questions) * 100) : 0

            catStats[catName] = { correct, total: resp.length, weight, percent }
          }

          // Calculate weighted score: sum(percent * weight) / 100
          weightedScore = Number(
            Object.values(catStats)
              .reduce((sum, { percent, weight }) => sum + (percent * weight) / 100, 0)
              .toFixed(2)
          )
        }

        // F. Final Score - use weighted if categories exist, otherwise raw
        const finalScore = Object.keys(categories).length > 0 ? weightedScore : rawScore

        return {
          id: t.id,
          status: t.status,
          created_at: t.created_at,
          started_at: t.started_at,
          completed_at: t.completed_at,
          assignment_start_date: t.assignment_start_date,
          assignment_due_date: t.assignment_due_date,
          employee: t.employee,
          attempt_count: t.attempt_count || 1,
          total_cheat_events: t.total_cheat_events,
          score: finalScore,
          totalCorrect: totalCorrect,
          totalQuestions,
          totalSeen,
          answeredCount,
          wrongCount,
          blankCount,
          categories,
          catStats,
        }
      })

      setAssignments(processedData)
      setLoading(false)
    }
    fetchData()
  }, [testId, searchTerm, magazaQuery, statusFilter])

  // Magaza suggestions from currently loaded data, filtered by pending input
  const magazaOptions = Array.from(
    new Set(assignments.map((r) => r.employee?.magaza).filter(Boolean))
  ).sort()
  const filteredMagazaOptions = magazaOptions.filter((m) =>
    m.toLocaleLowerCase('tr').includes(magazaInput.toLocaleLowerCase('tr'))
  )

  // Sorting Logic (filtering is done server-side)
  const sortedAssignments = [...assignments].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      case 'oldest':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      case 'nameAsc':
        return (a.employee?.personel || '').localeCompare(b.employee?.personel || '')
      case 'nameDesc':
        return (b.employee?.personel || '').localeCompare(a.employee?.personel || '')
      case 'scoreHigh':
        return (b.score || 0) - (a.score || 0)
      case 'scoreLow':
        return (a.score || 0) - (b.score || 0)
      case 'cheatHigh':
        return (b.total_cheat_events || 0) - (a.total_cheat_events || 0)
      case 'cheatLow':
        return (a.total_cheat_events || 0) - (b.total_cheat_events || 0)
      default:
        return 0
    }
  })

  // Pagination Logic
  const totalPages = Math.ceil(sortedAssignments.length / itemsPerPage)
  const paginatedAssignments = sortedAssignments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // Reset page when sort or page size changes (search/filter resets happen in handleSearch)
  useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, itemsPerPage, statusFilter])

  // Fetch all cheat events with full pagination (no 1000-row Supabase limit)
  const fetchAllCheatEvents = async (employeeTestIds: string[]) => {
    if (employeeTestIds.length === 0) return {}

    setFetchingCheatEvents(true)
    const BATCH_SIZE = 20

    try {
      let allCheatEvents: any[] = []

      // Split IDs into batches to avoid URL length limits,
      // but use fetchAllRows within each batch to bypass the 1000-row default limit
      for (let i = 0; i < employeeTestIds.length; i += BATCH_SIZE) {
        const batch = employeeTestIds.slice(i, i + BATCH_SIZE)
        try {
          const batchData = await fetchAllRows<any>(
            'cheat_events',
            'employee_test_id, event_type, verified',
            (q) => q.in('employee_test_id', batch)
          )
          allCheatEvents = allCheatEvents.concat(batchData)
        } catch (batchErr) {
          console.error('Error fetching cheat events batch:', batchErr)
        }
      }

      const cheatCountsByEmployeeTest: Record<
        string,
        Record<string, number>
      > = allCheatEvents.reduce((acc: Record<string, Record<string, number>>, ev: any) => {
        const etId = ev.employee_test_id as string
        const type = ev.event_type as string
        if (!acc[etId]) acc[etId] = {}
        acc[etId][type] = (acc[etId][type] || 0) + 1
        return acc
      }, {})

      setCheatEventsCache(cheatCountsByEmployeeTest)
      return cheatCountsByEmployeeTest
    } catch (err) {
      console.error('Error fetching all cheat events:', err)
      return {}
    } finally {
      setFetchingCheatEvents(false)
    }
  }

  // Cheat events are fetched on-demand during export, not pre-fetched

  const calculateTimeSpent = (startedAt: string | null, completedAt: string | null): string => {
    if (!startedAt || !completedAt) return '-'

    const start = new Date(startedAt).getTime()
    const end = new Date(completedAt).getTime()
    const diffMs = end - start

    if (diffMs < 0) return '-'

    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000)

    if (hours > 0) {
      return `${hours}s ${minutes}d`
    }
    return `${minutes}d ${seconds}sn`
  }

  // Export Function
  const exportResults = async () => {
    if (!testId) return

    setExportLoading(true)

    try {
      // Use the filtered + sorted list so export matches the UI
      const currentList = sortedAssignments

      // If filters hide all rows, still export an empty sheet with headers (and give feedback)
      if (currentList.length === 0) {
        toast.info('Filtrelere uygun aday bulunamadı. Sadece başlıklar dışa aktarılacak.')
      }

      // All employee_test ids for this export
      const employeeTestIds = currentList.map((a) => a.id).filter(Boolean)

      // Use cached cheat events or fetch if not available
      let cheatCountsByEmployeeTest = cheatEventsCache

      if (Object.keys(cheatCountsByEmployeeTest).length === 0 && employeeTestIds.length > 0) {
        cheatCountsByEmployeeTest = await fetchAllCheatEvents(employeeTestIds)
      }
      const allCategories = new Set<string>()
      currentList.forEach((item) => {
        if (item.catStats) {
          Object.keys(item.catStats).forEach((cat) => allCategories.add(cat))
        }
      })
      const categoryNames = Array.from(allCategories).sort()

      // Base headers (one row per candidate)
      const baseHeaders = [
        'Aday Adı',
        'Sicil',
        'Mağaza',
        'Kopya Şüphesi',
        'Atanma Tarihi',
        'Başlama Tarihi',
        'Bitiş Tarihi',
        'Sınavda Kalınan Süre',
        'Durum',
        'Puan',
        'Cevapladığı Soru Sayısı',
        'Doğru Sayısı',
        'Yanlış Sayısı',
        'Boş Sayısı',
        'Toplam Soru',
        'Başarı Oranı (%)',
      ]

      // Add category columns - 3 columns per category: Points Earned, Max Points, Percentage
      const categoryHeaders: string[] = []
      categoryNames.forEach((catName) => {
        categoryHeaders.push(`${catName} - Puan`)
        categoryHeaders.push(`${catName} - Maks Puan`)
        categoryHeaders.push(`${catName} - Oran (%)`)
      })

      // One column per cheat type, using labels from CHEAT_TYPE_MAP
      const cheatColumns = Object.keys(CHEAT_TYPE_MAP).map((key) => CHEAT_TYPE_MAP[key])

      const headers = [...baseHeaders, ...categoryHeaders, ...cheatColumns]

      // Build rows
      const rows = currentList.map((item) => {
        const totalQuestions = item.totalQuestions || 0
        const totalCorrect = item.totalCorrect || 0
        const successRate = totalQuestions ? Math.round((totalCorrect / totalQuestions) * 100) : 0

        const eventCounts = cheatCountsByEmployeeTest[item.id] || {}

        // Calculate total cheat events from cached data
        const totalCheatEvents = Object.values(eventCounts).reduce((sum, count) => sum + count, 0)
        const cheatValues = Object.keys(CHEAT_TYPE_MAP).map((key) => {
          const count = eventCounts[key] || 0
          return count > 0 ? String(count) : ''
        })

        const startedAtForExport = item.started_at || item.assignment_start_date
        const completedAtForExport = item.completed_at || item.assignment_due_date
        const timeSpent = calculateTimeSpent(item.started_at, item.completed_at)

        const baseRow = [
          item.employee?.personel || '',
          item.employee?.sicil || '',
          item.employee?.magaza || '',
          totalCheatEvents || '',
          formatLocalDate(item.created_at),
          formatLocalDate(startedAtForExport),
          formatLocalDate(completedAtForExport),
          timeSpent,
          getStatusText(item.status),
          item.score ?? '',
          item.answeredCount,
          item.totalCorrect,
          item.wrongCount,
          item.blankCount,
          totalQuestions,
          successRate,
        ]

        // Add category stats - for each category: points earned, max points, percentage
        const categoryValues: (string | number)[] = []
        categoryNames.forEach((catName) => {
          const catStat = item.catStats?.[catName]
          if (catStat) {
            // Points earned = (percentage / 100) * weight
            const pointsEarned = Number(((catStat.percent / 100) * catStat.weight).toFixed(2))
            const maxPoints = catStat.weight
            const percentage = catStat.percent

            categoryValues.push(pointsEarned)
            categoryValues.push(maxPoints)
            categoryValues.push(percentage)
          } else {
            categoryValues.push('')
            categoryValues.push('')
            categoryValues.push('')
          }
        })

        return [...baseRow, ...categoryValues, ...cheatValues]
      })

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sonuçlar')

      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      })

      saveAs(blob, `sonuclar-${testName}-${new Date().toISOString().split('T')[0]}.xlsx`)
      toast.success('Sonuçlar başarıyla dışa aktarıldı')
    } catch (err) {
      console.error('Error exporting results:', err)
      toast.error('Dışa aktarma sırasında bir hata oluştu')
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/results')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Sınavlara Dön
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{testName || '...'}</h1>
              <p className="text-gray-500">Aday Listesi</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={exportResults}
            disabled={assignments.length === 0 || exportLoading || fetchingCheatEvents}
          >
            {exportLoading || fetchingCheatEvents ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {fetchingCheatEvents}
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                Dışa Aktar
              </>
            )}
          </Button>
        </div>

        {/* Filters Toolbar */}
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6 bg-gray-50 p-4 rounded-lg border">
          {/* Left: Searches */}
          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-48">
              <Input
                placeholder="Aday veya sicil ara..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-white"
              />
            </div>

            <div className="relative w-full sm:w-48">
              <Input
                placeholder="Mağaza ara..."
                value={magazaInput}
                onChange={(e) => {
                  setMagazaInput(e.target.value)
                  setShowMagazaSuggestions(true)
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                onFocus={() => setShowMagazaSuggestions(true)}
                onBlur={() => setTimeout(() => setShowMagazaSuggestions(false), 150)}
                className="bg-white"
              />
              {showMagazaSuggestions && filteredMagazaOptions.length > 0 && (
                <div className="absolute bg-white border mt-1 w-full max-h-48 overflow-auto z-20 rounded shadow-md">
                  {filteredMagazaOptions.map((m) => (
                    <div
                      key={m}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onMouseDown={() => {
                        setMagazaInput(m)
                        setMagazaQuery(m)
                        setCurrentPage(1)
                        setShowMagazaSuggestions(false)
                      }}
                    >
                      {m}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={handleSearch} className="shrink-0">
              <Search className="h-4 w-4 mr-1" />
              Ara
            </Button>

            <div className="w-full sm:w-40">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-white w-full">
                  <SelectValue placeholder="Durumlar" />
                </SelectTrigger>
                <SelectContent>
                  {statusFilterOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Right: Sort & Pagination */}
          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">Sırala:</span>
              <Select value={sortBy} onValueChange={(val) => setSortBy(val as SortOption)}>
                <SelectTrigger className="w-[160px] bg-white">
                  <SelectValue placeholder="Sıralama" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">En Yeni</SelectItem>
                  <SelectItem value="oldest">En Eski</SelectItem>
                  <SelectItem value="nameAsc">İsim (A-Z)</SelectItem>
                  <SelectItem value="nameDesc">İsim (Z-A)</SelectItem>
                  <SelectItem value="scoreHigh">En Yüksek Puan</SelectItem>
                  <SelectItem value="scoreLow">En Düşük Puan</SelectItem>
                  <SelectItem value="cheatHigh">En Yüksek Kopya Şüphesi</SelectItem>
                  <SelectItem value="cheatLow">En Düşük Kopya Şüphesi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">Sayfa başı:</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(val) => setItemsPerPage(Number(val))}
              >
                <SelectTrigger className="w-[80px] bg-white">
                  <SelectValue placeholder="10" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <div className="rounded-2xl shadow-lg border border-gray-200 bg-white overflow-hidden mb-4">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="p-4 text-left font-medium text-gray-600">Aday</th>
                  <th className="p-4 text-left font-medium text-gray-600">Sicil</th>
                  <th className="p-4 text-left font-medium text-gray-600">Kopya Şüphesi</th>
                  <th className="p-4 text-left font-medium text-gray-600">Bitiş Tarihi</th>
                  <th className="p-4 text-left font-medium text-gray-600">Durum</th>
                  <th className="p-4 text-left font-medium text-gray-600">Puan</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-12 text-center">
                      <div className="flex items-center justify-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        <span className="font-medium">Yükleniyor...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedAssignments.length > 0 ? (
                  paginatedAssignments.map((item, idx) => (
                    <tr
                      key={item.id}
                      className={
                        'hover:bg-blue-50 cursor-pointer transition-colors ' +
                        (idx !== paginatedAssignments.length - 1 ? 'border-b border-gray-100' : '')
                      }
                      onClick={() => {
                        if (item.status === 'expired') return
                        navigate(`/admin/results/${testId}/${item.id}`)
                      }}
                    >
                      <td className="p-4 font-medium text-gray-900">
                        {item.employee?.personel || '-'}
                      </td>
                      <td className="p-4 text-gray-600">{item.employee?.sicil || '-'}</td>
                      <td className="p-4 text-gray-600">{item.total_cheat_events ?? '-'}</td>
                      <td className="p-4 text-gray-600">
                        {item.started_at
                          ? formatLocalDate(item.started_at)
                          : formatLocalDate(item.assignment_due_date)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(item.status)}
                          <span className="text-sm text-gray-700">
                            {getStatusText(item.status)}
                          </span>
                          {item.attempt_count > 1 && (
                            <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                              D{item.attempt_count}/4
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {item.score !== null &&
                        (item.status === 'completed' || item.status === 'timeout') ? (
                          <>{item.score}</>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      {item.status !== 'expired' && (
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                          >
                            Detay
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="p-12 text-center text-gray-500">
                      Aday bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && !loading && (
          <div className="flex justify-center items-center gap-2 mt-4 pb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Önceki
            </Button>

            {/* Simplified page numbers */}
            <div className="flex gap-1">
              {[...Array(totalPages)].map((_, idx) => {
                // Show first, last, and pages around current
                if (
                  idx === 0 ||
                  idx === totalPages - 1 ||
                  (idx >= currentPage - 2 && idx <= currentPage)
                ) {
                  return (
                    <Button
                      key={idx}
                      variant={currentPage === idx + 1 ? 'default' : 'outline'}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setCurrentPage(idx + 1)}
                    >
                      {idx + 1}
                    </Button>
                  )
                } else if (
                  (idx === currentPage - 3 && currentPage > 3) ||
                  (idx === currentPage + 1 && currentPage < totalPages - 2)
                ) {
                  return (
                    <span key={idx} className="px-1 py-1 text-gray-400">
                      ...
                    </span>
                  )
                }
                return null
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Sonraki
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminTestCandidates
